import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaRocket, 
  FaUsers, 
  FaAward, 
  FaLightbulb, 
  FaCode, 
  FaGlobe,
  FaArrowRight
} from 'react-icons/fa';
import './About.css';

const About = () => {
  const stats = [
    { number: "50+", label: "Projects Completed", icon: <FaCode /> },
    { number: "15+", label: "Happy Clients", icon: <FaUsers /> },
    { number: "3+", label: "Years Experience", icon: <FaRocket /> },
    { number: "100%", label: "On-Time Delivery", icon: <FaAward /> }
  ];

  const values = [
    {
      icon: <FaLightbulb />,
      title: "Innovation First",
      description: "We constantly push the boundaries of technology to deliver cutting-edge solutions that set new industry standards."
    },
    {
      icon: <FaUsers />,
      title: "Client-Centric",
      description: "Your success is our priority. We work closely with you to understand your vision and bring it to life."
    },
    {
      icon: <FaRocket />,
      title: "Fast Delivery",
      description: "We believe in agile development and rapid deployment without compromising on quality or functionality."
    },
    {
      icon: <FaGlobe />,
      title: "Global Reach",
      description: "Serving clients worldwide with scalable solutions that work seamlessly across different markets and cultures."
    }
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="about" className="about">
      <div className="container">
        {/* About Header */}
        <motion.div
          className="about-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="section-badge">About Us</span>
          <h2 className="section-title">
            Pioneering the Future of <span className="text-gradient">Digital Innovation</span>
          </h2>
          <p className="section-description">
            At Infinity Logs, we're not just building software – we're crafting the digital future. 
            Our team of passionate developers and designers work tirelessly to create solutions that 
            transform businesses and delight users.
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="stat-card"
              variants={itemVariants}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Mission & Vision */}
        <motion.div
          className="mission-vision"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="mission-card">
            <div className="card-header">
              <FaRocket className="card-icon" />
              <h3>Our Mission</h3>
            </div>
            <p>
              To empower businesses with innovative technology solutions that drive growth, 
              enhance user experiences, and create lasting digital transformations in an 
              ever-evolving technological landscape.
            </p>
          </div>

          <div className="vision-card">
            <div className="card-header">
              <FaLightbulb className="card-icon" />
              <h3>Our Vision</h3>
            </div>
            <p>
              To be the leading force in digital innovation, setting new standards for 
              excellence in web development, mobile applications, and technology platforms 
              that shape the future of business.
            </p>
          </div>
        </motion.div>

        {/* Values Section */}
        <motion.div
          className="values-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <h3 className="values-title">Our Core Values</h3>
          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="value-icon">{value.icon}</div>
                <h4 className="value-title">{value.title}</h4>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>


        {/* CTA Section */}
        <motion.div
          className="about-cta"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h3>Ready to Start Your Digital Journey?</h3>
            <p>Join the growing number of businesses that trust Infinity Logs for their technology needs.</p>
            <motion.a 
              href="#contact"
              className="cta-btn"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Today
              <FaArrowRight className="btn-icon" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
